import {
	FINANCING_BANK_NAME_ENUM,
	ROBOTIC_SIGNING_STATUS,
	DOCUMENT_TRACKING_STATUS,
	DOCUMENT_PACKET_TYPES,
} from '../constants';
import { DiscountCategory } from './CheckoutCart.js';
import { PaymentObject } from './PaymentObject.js';
import { LenderDecision } from './financing/LenderDecision.js';
import { FinalTerms } from './financing/FinalTerms.js';
import { CreditApplication } from './financing/CreditApplication.js';
import { Services } from './ServicesProvider';
import { ActionItemAgent, ActionItemStatus } from './ActionItem';
import { StateAbbreviation } from './StateAbbreviation';

export enum VehicleType {
	PASSENGER = 'Passenger',
	MOTORCYCLE = 'Motorcycle',
	DUAL_SPORT = 'DualSport',
	TRUCK = 'Truck',
	COMMERCIAL = 'Commercial',
	TRAILER = 'Trailer',
	MOPED = 'Moped',
	OHV = 'OHV',
	VESSEL = 'Vessel',
	NEV = 'NeighborhoodElectricVehicle',
	LOW_SPEED = 'LowSpeed',
	GO_KART = 'Gokart',
	E_BIKE = 'E-Bike',
	BICYCLE = 'Bicycle',
	ATV = 'ATV',
	ORV = 'ORV',
}

export enum VehicleStage {
	SIGN_OFF = 'signOff',
	PENDING_PAYMENT_GUARANTEE = 'pendingPaymentGuarantee',
	PENDING_VERIFICATION = 'pendingVerification',
	PRODUCTION = 'production',
	COMPLETE = 'complete',
}

export enum IdDocumentType {
	DRIVING_LICENSE = 'driving_license',
	PASSPORT = 'passport',
	ID_CARD = 'id_card',
}

export enum VehicleExternalStatus {
	UNCATEGORIZED = 'UNCATEGORIZED',
	PREORDER = 'PREORDER',
	CANCELED = 'CANCELED',
	ON_HOLD = 'ON_HOLD',
	PENDING = 'PENDING',
	AWAITING_OEM_ACTION = 'AWAITING_OEM_ACTION',
	AWAITING_BUYER_OR_EKHO_ACTION = 'AWAITING_BUYER_OR_EKHO_ACTION',
	KICK_OFF_FULFILLMENT = 'KICK_OFF_FULFILLMENT',
	FULFILLMENT_IN_PROGRESS = 'FULFILLMENT_IN_PROGRESS',
	FULFILLED = 'FULFILLED',
}

export enum VehicleFulfillmentStatus {
	UNFULFILLED = 'UNFULFILLED',
	FULFILLED = 'FULFILLED',
	READY_TO_FULFILL = 'READY_TO_FULFILL',
	CANCELED = 'CANCELED',
	ON_HOLD = 'ON_HOLD',
	IN_TRANSIT_TO_BUYER = 'IN_TRANSIT_TO_BUYER',
	IN_TRANSIT_TO_FULFILLMENT_CENTER = 'IN_TRANSIT_TO_FULFILLMENT_CENTER',
	RECEIVED_AT_FULFILLMENT_CENTER = 'RECEIVED_AT_FULFILLMENT_CENTER',
	PICKUP_SCHEDULED = 'PICKUP_SCHEDULED',
	READY_TO_DISPATCH = 'READY_TO_DISPATCH',
}

export enum VehiclePaymentSelection {
	NATIVE_FINANCING = 'nativeFinancing',
	OTHER_FINANCING = 'otherFinancing',
	CHECKOUT_FULL_PAYMENT = 'checkoutFullPayment',
	CASH_EARLY = 'cashEarly',
	CASH = 'cash',
	KLARNA_CHECKOUT_FULL_PAYMENT = 'klarnaCheckoutFullPayment',
}

export interface PurchasedVehicleAccessory {
	categoryId: string;
	categoryName: string;
	id: string;
	imgUrl: string;
	name: string;
	price: number;
	productId: string;
	partNumber?: string;
	supplierCode?: string;
}

/**
 * PurchasedVehicle interface
 * Represents a purchased vehicle along with its associated data and metadata.
 */
export interface PurchasedVehicle {
	/** accessories */
	accessories: PurchasedVehicleAccessory[];
	/** Unique identifier for the purchased vehicle */
	id: string;
	/** The product id associated with this vehicle */
	productId: string;
	/**
	 * The government vehicle type
	 */
	vehicleType?: VehicleType;
	/** The id of the buyer */
	buyerId: string;
	/**
	 * The full name of the buyer (copied from the buyer object).
	 * DO NOT use this for T&R, instead use the names stored in registrationDetails
	 */
	buyerFullName: string;
	/**
	 * The last time the buyer viewed the purchased vehicle's details page. Used for nudging.
	 */
	lastActive?: number;
	/** The order id placed for this vehicle */
	placedOrderId: string;
	/**
	 * How much a buyer pays at checkout towards a vehicle. In case of full vehicle payment, this is the total price of the vehicle
	 * (without any taxes or fees included and minus any previous payments - like preorder fees).
	 * In case of financing, this is the standard down payment (not inclusive of preorder fees).
	 * In case of standard reservation, this is the reservation fee.
	 */
	orderFee: number;
	/** The name of the vehicle */
	name: string;
	/** The base price of the vehicle */
	basePrice: number;
	/** The base price of the vehicle including the price of any specs */
	basePriceWithSpecs: number;
	/**
	 * Array of specs for the vehicle.
	 * Each spec includes name, price, option. Description and id are optional.
	 */
	specs: {
		/** The name of the spec */
		name: string;
		/** The price of the spec */
		price: number;
		/** The option chosen for the spec */
		option: string;
		/**
		 * The description of the spec. Allows for a longer string and shows up in a tooltip on the frontend
		 */
		description?: string;
		/**
		 * The id of the spec. Never used by Ekho, but could be useful for OEMs
		 */
		id?: string;
	}[];
	imgUrl: string;
	description: string;
	oemId: string;
	typeOfOrder: 'preorder' | 'buildToOrder' | 'inventoryOrder';
	/** The date the reservation fee was paid for this vehicle (ms since epoch). Example: 1699246800000 */
	datePurchased: number;
	/**
	 * The number of total vehicles made ready for production by the OEM at the time of vehicle creation.
	 * internal-only
	 */
	numPrevVehiclesTowardTiers: number;
	/**
	 * What is the status of the order from a production standpoint?
	 */
	stage: VehicleStage;
	/**
	 * A reflection of where the order is located in the fulfillment process
	 *
	 * FULFILLED - Vehicle has been handed over to buyer and all action items are complete
	 * UNFULFILLED - Vehicle is not yet ready to begin the fulfillment process
	 * READY_TO_FULFILL - Vehicle is ready to begin the fulfillment process — meaning depends on the type of order (see checkReadyToFulfill())
	 * CANCELED - Order has been canceled either by buyer if cancellable or Ekho
	 * ON_HOLD - Order temporarily put on hold, usually due to a dispute or manufacturing constraint
	 * IN_TRANSIT_TO_BUYER - Order fulfillment type is delivery and vehicle is in transit to buyer
	 * IN_TRANSIT_TO_FULFILLMENT_CENTER - Order fulfillment type is pickup or delivery, vehicle is in transit to fulfillment center
	 * PICKUP_SCHEDULED - Order fulfillment type is pickup, fulfillment center has contacted buyer to set pickup time
	 * READY_TO_DISPATCH - For orders going to fulfillment center (Omni), giving OEM the green light to ship to fulfillment center (see checkReadyToDispatch())
	 */
	vehicleFulfillmentStatus: VehicleFulfillmentStatus;
	/**
	 * The following optional properties are provided by the OEM at time of setting vehicleFulfillmentStatus to IN_TRANSIT_TO_BUYER or IN_TRANSIT_TO_FULFILLMENT_CENTER.
	 */
	fulfillmentDetails?: {
		/** Tracking number or link. */
		vehicleTrackingReference?: string;
		/** A number the buyer can call. */
		contactNumber?: string;
		/** Name of the fulfillment company - presumably a delivery vendor. */
		companyName?: string;
		/** Tracking number or link for MSO. */
		msoTrackingReference?: string;
		/** Init as false. Indicates whether the OEM has sent us the MSO. */
		msoSent?: boolean;
		/** Init as false. Indicates whether the OEM has completed the MSO. */
		msoCompleted?: boolean;
		/** file path to the completed MSO */
		msoFilePath?: string;
	};
	/**
	 * The following optional properties are provided by the OEM at time of setting vehicleFulfillmentStatus to IN_TRANSIT_TO_FULFILLMENT_CENTER.
	 */
	transitToFulfillmentCenterDetails?: {
		/** Tracking number or link. */
		vehicleTrackingReference?: string;
		/** A number the dealer can call. */
		contactNumber?: string;
		/** Name of the fulfillment company - presumably a delivery vendor. */
		companyName?: string;
		/**
		 * The estimated date of when this vehicle will be delivered to the services provider (pickup or delivery)
		 * in ms since epoch. Only used when a services provider is assigned to handle fulfillment.
		 */
		estimatedDeliveryDateToFulfillmentCenter?: number;
		/**
		 * The date the vehicle was received at the fulfillment center in ms since epoch.
		 */
		dateReceivedAtFulfillmentCenter?: number;
	};
	/**
	 * Object containing inspection details for this vehicle
	 */
	inspection?: {
		/** Whether inspection has been completed */
		isComplete: boolean;
		/** Date inspection was completed in ms since epoch */
		dateInspected: number;
		/** Name/ID of agent who performed inspection */
		agent: string;
		/** Any notes from inspection */
		notes: string;
	};
	/**
	 * Object containing assembly details for this vehicle
	 */
	assembly?: {
		/** Whether assembly has been completed */
		isComplete: boolean;
		/** Date assembly was completed in ms since epoch */
		dateInspected: number;
		/** Name/ID of agent who performed assembly */
		agent: string;
		/** Any notes from assembly */
		notes: string;
	};
	/** internal-only */
	cancelationAgent?: 'BUYER_CANCELED' | 'EKHO_CANCELED';
	/**
	 * Payment info object for this vehicle
	 * Includes details about chosen payment method, vehicle payment status, financing details, discounts, etc.
	 */
	paymentInfo: {
		/**
		 * Payment selection the buyer has chosen. Initially set during checkout and can be changed in financing. Init as null.
		 */
		paymentSelection: VehiclePaymentSelection;
		/**
		 * The status of payment for this particular purchasedVehicle. Init as null.
		 */
		vehiclePaymentStatus:
			| 'RESERVATION_INITIATIED'
			| 'NO_PAYMENTS'
			| 'RESERVATION_PAID'
			| 'DOWNPAYMENT_PAID'
			| 'FINANCING_GUARANTEED'
			| 'FULLY_PAID';
		/**
		 * Map containing all information about native financing
		 * internal-only
		 */
		nativeFinancing?: {
			/**
			 * Buyer's selected loan option. Init as null
			 */
			buyerPreference?: {
				/** annual percentage rate */
				apr: number;
				/**
				 * Includes orderFee (which has already been paid)
				 */
				downPayment: number;
				/**
				 * The amount financed. Equals totalPayment + taxTotal + feeTotal + deliveryFeeEstimate - downPayment
				 */
				loanAmount: number;
				/**
				 * The length of the loan in months
				 */
				loanTerm: number;
				/** monthly payment amount */
				monthlyPayment: number;
				/** name of the loan option (e.g. Pay Less Now) */
				name: string;
			};
			/**
			 * FRF decision on a credit application. Init as null
			 * Each key is an entry in FINANCING_BANK_NAME_ENUM and the associated value is an object containing lender decision for a particular bank
			 */
			lenderDecision: {
				[bankName in keyof typeof FINANCING_BANK_NAME_ENUM]?: LenderDecision;
			};
			/**
			 * Object representing a payment. Referenced from "#/components/schemas/PaymentObject"
			 * Since no schema is provided, assume PaymentObject is defined elsewhere.
			 */
			downPayment?: PaymentObject;
			/**
			 * Final terms of the loan. Init as null. Will only be set with final additional dues.
			 */
			finalTerms?: FinalTerms | null;
			/**
			 * Have we received the financed amount from the lender? Init as false
			 */
			receivedLoan: boolean;
			/**
			 * Action item for financing. Init as null.
			 */
			financingActionItem?:
				| 'SUBMIT_CREDIT_APP_AND_REPORT_DECISION'
				| 'GATHER_ADDITIONAL_INFO'
				| 'PERFORM_PREAUDIT'
				| 'SIGN_DOCS'
				| 'SUBMIT_FUNDING_PACKAGE'
				| 'BLOCKED'
				| 'COMPLETE'
				| null;
			/**
			 * Prequalification information for buyers who went through the flow. Not present if buyer didn't try to prequalify.
			 */
			prequalification?: {
				status: 'APPROVED' | 'DENIED';
				/**
				 * Array of reasons why the buyer was rejected. Only present if status is denied.
				 */
				rejectionReasons?: string[];
			};
			/**
			 * Indicates whether the funding packet has been submitted to the lender. Init as false.
			 */
			submittedFundingPacket: boolean;
		};
		/**
		 * Running total of all money received by Ekho for this vehicle (post refunds).
		 * Not all money accounted here will necessarily be collected by Ekho.
		 * Initialized at 0.
		 */
		totalAmountReceived: number;
		/**
		 * Reservation payment object. See PaymentObject.
		 * internal-only
		 */
		reservationPayment?: PaymentObject;
		/**
		 * Cash payment object. See PaymentObject.
		 * internal-only
		 */
		cashPayment?: PaymentObject;
		/**
		 * An array of objects that hold information about each discount applied to this vehicle.
		 * Initialized as an empty array.
		 * internal-only
		 */
		discounts?: {
			/** The amount of the discount in dollars */
			amount: number;
			type: 'PERCENTAGE_DISCOUNT' | 'FIXED_AMOUNT_DISCOUNT' | 'SHIPPING_DISCOUNT';
			/** The human readable name of the discount */
			name: string;
			/** The date the discount was applied in ms since epoch */
			dateApplied: number;
			category: DiscountCategory;
			/** Any notes about the discount */
			notes?: string;
			/** The id of the campaign that the discount was applied from. Null if not a discount campaign discount. */
			campaignId: string;
			/** The id of the re-engagement campaign that the discount was applied from. Null if not a re-engagement discount. */
			reid: string;
		}[];
		/**
		 * array of objects that hold information about each vehicle payment made before checkout
		 * internal-only
		 */
		amountsPaidBeforeCheckout?: {
			/** The amount of the payment in dollars */
			amount: number;
			type: 'PREORDER' | 'EXISTINGORDER';
			recipient: 'OEM' | 'EKHO';
		}[];
		/**
		 * Running total of all money paid by the buyer for this vehicle (post refunds).
		 * Injected via the addRollUpFieldsToVehicle function.
		 * internal-only
		 */
		totalAmountPaidByBuyer?: number;
		/**
		 * Running total of all money received by Ekho for this vehicle for the dealer reserve.
		 * Injected via the addRollUpFieldsToVehicle function.
		 * internal-only
		 */
		totalAmountReceivedInDealerReserve?: number;
		/**
		 * The date the vehicle's vehiclePaymentStatus was set to FULLY_PAID. No initial value.
		 * internal-only
		 */
		fullyPaidDate?: number;
		/**
		 * other payments array, each entry is a PaymentObject
		 * internal-only
		 */
		otherPayments?: PaymentObject[];
	};
	/** The gross invoice price of the vehicle paid by Ekho to the client */
	grossInvoicePrice: number;
	/**
	 * Indicates whether sensitive data has been purged from this vehicle. Init as false.
	 */
	sensitiveDataPurged: boolean;
	/** The variant id of the vehicle */
	variantId: string;
	/** The SKU of the vehicle */
	sku: string;
	/**
	 * An array with the document IDs of boltOn purchasedAccessories that have been assigned to this vehicle
	 */
	boltOnAccessories: string[];
	/**
	 * The price of the vehicle plus the price of each bolt on accessory minus any discounts!
	 * Does not include taxes or fees.
	 */
	totalPrice: number;
	/**
	 * The price of the vehicle plus the price of each bolt on accessory before any discounts are subtracted!
	 * Does not include taxes or fees.
	 */
	preDiscountTotalPrice: number;
	/**
	 * Determines the type of order this vehicle belongs to. Defaults to "ORGANIC".
	 */
	orderCreationType: 'ORGANIC' | 'PREORDER' | 'EXISTINGORDER' | 'OEMORIGINATED' | 'ABANDONEDCART';
	/**
	 * The mutually exclusive external (OEM-facing) status that an "order" (purchasedVehicle object) can be in.
	 * Used in the OEM Admin Portal to filter orders.
	 */
	externalStatus: VehicleExternalStatus;
	/**
	/**
	 * The estimated date of when this vehicle will be finished being produced in ms since epoch.
	 * Default initial value to purchasedDate + Vehicle.averageProductionTime
	 */
	estimatedProductionDate: number;
	/**
	 * The estimated date of when this vehicle will be delivered (to the buyer or to the pick up location)
	 * in ms since epoch. Default initial value will be purchasedDate + Vehicle.totalLeadTime
	 */
	estimatedDeliveryDate: number;
	/**
	 * The estimated date of when this vehicle will be dispatched (either to the fulfillment center or to the buyer)
	 * in ms since epoch.
	 */
	estimatedDispatchDate: number;
	/**
	 * Array of relocations objects for this vehicle. Init as empty array.
	 * internal-only
	 */
	relocations: {
		/**
		 * Name of relocation flow to be displayed. Corresponds to the RELOCATION_FLOWS value in constants.js file
		 */
		name: string;
		/** unique id of the relocation object. */
		id: string;
		/**
		 * True if the relocation flow has been started for this vehicle. False otherwise.
		 */
		started: boolean;
		/**
		 * Section of bp that buyer will return to after relocation flow is complete
		 */
		reEntrySection: string;
		/**
		 * Page of bp that buyer will return to after relocation flow is complete.
		 * Null if reEntry page was null at time of relocation
		 */
		reEntryPage: string;
		/**
		 * Describes the behavior of different pages in the relocation flow.
		 * Allows us to override the default behavior of a page.
		 * Always contains at least two entries - the initiation page and the termination page.
		 */
		relocPageBehavior: {
			[key: string]: {
				/**
				 * Present if this page is the initiation page for the relocation flow.
				 * If true, the buyer will be redirected to this page when they click the relocation link.
				 * A relocation flow must always have a (single) initiation page.
				 */
				initPage?: boolean;
				/**
				 * Associated section the initiation page belongs to. Must be present if this is the initiation page.
				 */
				initSection?: string;
				/**
				 * Present if this page is the termination page for the relocation flow.
				 * A relocation flow must always have at least one termination page.
				 */
				terminationPage?: boolean;
				/**
				 * Present if the back button should be disabled on this page.
				 * If true, the buyer will not be able to go back to the previous page.
				 * This is default behavior for the init page.
				 */
				backButtonDisabled?: boolean;
				/**
				 * Overrides the choices in the choices page.
				 * Array of choice titles that should be included in relocation flow.
				 */
				choices?: string[];
			};
		};
		/**
		 * The id of the modal corresponding to the relocation flow. If present, the relocation flow will be initiated by the modal.
		 */
		sourceModalId?: string;
	}[];
	/**
	 * Array of modal objects to be shown in buyer portal for this vehicle. Init as empty array.
	 * internal-only
	 */
	modals: {
		/**
		 * Name of modal to be displayed. Corresponds to modal object in bp
		 */
		name: string;
		/**
		 * Unique id of the modal object
		 */
		id: string;
		/**
		 * True if the buyer has acknowledged the modal. False otherwise.
		 */
		acknowledged: boolean;
		/**
		 * Additional metadata for the modal
		 */
		metadata?: {
			[key: string]: string;
		};
	}[];
	/**
	 * The year of the vehicle model. This is only optional because of backwards compatibility.
	 * Gets injected at order creation time.
	 */
	year?: number;
	/**
	 * The date we put on dealership documents for when the vehicle was purchased.
	 * internal-only
	 */
	datePurchasedForDocuments?: number;
	/**
	 * At time of check out, if this field is set, it means the buyer has opted for pick up.
	 */
	pickUpInfo?:
		| {
				pickUpLocationId: string;
				pickUpLocationName: string;
				obfuscatedName: string;
				addressLineOne: string;
				addressLineTwo: string;
				county: string;
				city: string;
				state: StateAbbreviation;
				zip: string;
				providerId?: string;
		  }
		| undefined;
	/**
	 * At time of check out, if this field is set, it means the buyer has opted for delivery.
	 */
	deliveryInfo?:
		| {
				addressLineOne: string;
				addressLineTwo: string;
				county: string;
				city: string;
				state: StateAbbreviation;
				zip: string;
				deliveryInstructions?: string;
				poc?: string;
				pocName?: string;
				pocNumber?: number;
		  }
		| undefined;
	/**
	 * Object used to house admin around the submission for credit application
	 * internal-only
	 */
	creditApplication?: CreditApplication;

	/**
	 * Deprecated. Use registrationDetails.tarProcess object instead.
	 */
	tarProcess?: 'AUTOMATED' | 'ASSISTED' | 'AUTOMATED_TITLING' | 'ASSISTED_TITLING' | 'NONE';

	/**
	 * Registration details object
	 */
	registrationDetails: {
		/**
		 * The type of document used for identification.
		 * internal-only
		 */
		idDocumentType?: IdDocumentType | undefined;
		/**
		 * The process that the buyer has chosen for titling and registration.
		 */
		tarProcess?: 'AUTOMATED' | 'ASSISTED' | 'AUTOMATED_TITLING' | 'ASSISTED_TITLING' | 'NONE';
		/**
		 * This is the name of the agent that will be used for titling and registration.
		 * internal-only
		 */
		tarAgent: 'EKHO' | 'VI' | 'CG' | 'BUYER' | 'NONE';
		/** internal-only */
		registrantType?: string;
		/** internal-only */
		firstName?: string;
		/** internal-only */
		middleName?: string;
		/** internal-only */
		lastName?: string;
		/** internal-only */
		isOwnerMilitary?: boolean;
		/** internal-only */
		selfTarDocsSent?: {
			docsSent: boolean;
			trackingNumber?: string;
			status?: string;
		};
		/** internal-only */
		isOwnerResident?: boolean;
		/**
		 * Buyer's Social Security Number
		 * internal-only
		 */
		ssn?: string;
		/**
		 * Buyer's eye color, as described in their driver's license
		 * internal-only
		 */
		eyeColor?: string;
		/** internal-only */
		email?: string;
		/**
		 * Date of birth (encrypted string of milliseconds since UNIX epoch)
		 * internal-only
		 */
		dob?: string;
		/** internal-only */
		sex?: 'Male' | 'Female' | 'Other';
		/**
		 * Driver's license number
		 * internal-only
		 */
		dlNumber?: string;
		/**
		 * Driver's license two-letter capital abbreviated state name
		 * internal-only
		 */
		dlState?: string;
		/**
		 * Driver's license expiration date
		 * internal-only
		 */
		dlExpDate?: number;
		/**
		 * Driver's license address
		 * internal-only
		 */
		dlAddress?: {
			addressLineOne: string;
			addressLineTwo: string;
			city: string;
			county?: string;
			state: StateAbbreviation;
			zip: string;
		};
		/**
		 * Registration address
		 * internal-only
		 */
		registrationAddress: {
			addressString: string;
			addressLineOne: string;
			addressLineTwo: string;
			city: string;
			county: string;
			state: StateAbbreviation;
			zip: string;
		};
		/**
		 * Standardized Registration Address
		 * internal-only
		 */
		standardizedRegistrationAddress: {
			addressLineOne: string;
			addressLineTwo: string;
			city: string;
			county: string;
			state: StateAbbreviation;
			zip: string;
		};
		/** internal-only */
		standardizedCounty: string;
		/** internal-only */
		mailAddressSameAsReg?: boolean;
		/** internal-only */
		mailingAddress?: {
			addressString: string;
			addressLineOne: string;
			addressLineTwo: string;
			city: string;
			county: string;
			state: StateAbbreviation;
			zip: string;
		};
		/**
		 * Payment type, currently only PayInFull
		 * internal-only
		 */
		paymentType?: 'PayInFull';
		/**
		 * Plate category, currently only Standard
		 * internal-only
		 */
		plateCategory?: 'Standard';
		/**
		 * Two letter state abbreviation for registration state
		 */
		registrationState: StateAbbreviation;
		/** Optional. The birth year of the buyer in UNIX timestamp. */
		birthYear: number;
		/**
		 * Use type, currently Personal or Commercial
		 * internal-only
		 */
		useType?: 'Personal' | 'Commercial';
		/**
		 * Temp tag info
		 * internal-only
		 */
		tempTag?: {
			fileDownloadUrl?: string;
			expiryDate?: number;
			tempTagRequested?: boolean;
		};
		[key: string]: any; // additional properties allowed
	};
	/**
	 * map of maps containing links to different uploaded docs for titling and registration
	 * internal-only
	 */
	registrationFileUploads?: {
		id?: {
			doc1?: string;
			doc2?: string;
			[key: string]: any;
		};
		insurance?: {
			doc1?: string;
			doc2?: string;
			[key: string]: any;
		};
		[key: string]: any;
	};
	/**
	 * this object is deprecated and should only be present on old vehicles.
	 * New additional dues live in their own array
	 */
	additionalDues?: {
		/**
		 * the fidelity of the estimates for taxes and fees.
		 * internal-only
		 */
		fidelity: 'initial' | 'apiEstimate' | 'final';
		/**
		 * sum of fee estimates, tax estimates, and delivery estimates (if applicable)
		 * does not include wiggleroom!
		 */
		total: number;
		/** sum of tax estimates */
		taxTotal: number;
		/** sum of fee estimates */
		feeTotal: number;
		/**
		 * the estimated delivery cost of the vehicle. Should be 0 if picking up.
		 */
		deliveryEstimate: number;
		/**
		 * the estimated pickup cost of the vehicle. Should be 0 if vehicle is being delivered.
		 */
		pickUpEstimate: number;
		/** array of tax estimates
		 *  internal-only
		 */
		taxEstimates: {
			feeType: string;
			name: string;
			value: number;
		}[];
		/** array of fee estimates
		 *  internal-only
		 */
		feeEstimates: {
			feeType: string;
			name: string;
			value: number;
		}[];
	};
	/**
	 * VIN must be input by the OEM at some point prior to delivery
	 */
	vin?: string;
	/**
	 * Must be input by the OEM at some point prior to delivery, or can be auto set
	 */
	odometerReading?: number;
	/** Indicates the odometer validity/condition. Can be autoset */
	odometerValidity?: 'Actual' | 'ExceedsMechanicalLimits' | 'NotActual';
	/**
	 * Must be input by the OEM if the OEM is selling motorcycles
	 */
	motorcycleEngineNumber?: string;
	/**
	 * The URL for downloading the transaction packet (optional)
	 * internal-only
	 */
	transactionMetadata?: {
		url?: string;
		indices?: {
			[key: string]: string;
		};
	};
	/**
	 * transaction may exist without this having being written yet.
	 * Field only exists once VITU transaction is made.
	 * internal-only
	 */
	transactionId?: number;
	/**
	 * Updated any time transaction status changes. Field only exists once VITU transaction is made.
	 * Enum options are from VITU.
	 * internal-only
	 */
	transactionStatus?:
		| 'Started'
		| 'Draft'
		| 'Completed'
		| 'PreAuditInProgress'
		| 'PreAuditErrorsFound'
		| 'PreAuditCompleted'
		| 'SentToService'
		| 'Received'
		| 'ErrorsFound'
		| 'SentToState'
		| 'ReturnedFromState'
		| 'StateProcessed'
		| 'StateCompleted'
		| 'Inactive'
		| 'CompletedManually'
		| 'Canceled'
		| 'Failed';
	/**
	 * Array of objects that represent the steps that have already been completed for the document packet for this vehicle.
	 * Field only exists once VITU transaction is created.
	 * internal-only
	 */
	documentSteps?: (
		| 'GENERATED_BUYER_DOCUMENTS'
		| 'BUYER_SENT_DOCUMENTS'
		| 'OEM_SENT_DOCUMENTS'
		| 'LIENHOLDER_SENT_DOCUMENTS'
		| 'SELLER_RECEIVED_ALL_DOCUMENTS'
		| 'SELLER_RECEIVED_BUYER_DOCUMENTS'
		| 'SELLER_RECEIVED_OEM_DOCUMENTS'
		| 'SELLER_RECEIVED_LIENHOLDER_DOCUMENTS'
		| 'SELLER_SENT_DOCUMENTS'
		| 'FINALIZED_BUYER_PACKET'
	)[];
	/**
	 * boolean that indicates whether Ekho needs to review T&R forms.
	 * Field only exists once VITU transaction is created.
	 * internal-only
	 */
	formsNeedToBeReviewed?: boolean;
	/**
	 * The id of the cart that was used to check out this vehicle. Optional
	 */
	checkoutCartId?: string;
	/**
	 * Whether the internal intervention for self-registration transactions has been completed.
	 * Field only exists for self-registration transactions.
	 * internal-only
	 */
	completedSelfRegistrationIntervention?: boolean;
	/**
	 * Map of maps detailing the e-signature document sets that should be used for this purchased vehicle.
	 * Key is the page name related to each document set.
	 * internal-only
	 */
	esigs?: {
		nativeFinancingSignatures?: {
			documentSetMetadata: DocumentSetMetadata;
			[key: string]: DocumentProperties;
		};
		documentsSignatures?: {
			documentSetMetadata: DocumentSetMetadata;
			[key: string]: DocumentProperties;
		};
		tarSignatures?: {
			documentSetMetadata: DocumentSetMetadata;
			[key: string]: DocumentProperties;
		};
		purchaseAgreementSignatures?: {
			documentSetMetadata: DocumentSetMetadata;
			[key: string]: DocumentProperties;
		};
		financingContractSignatures?: {
			documentSetMetadata: DocumentSetMetadata;
			[key: string]: DocumentProperties;
		};
		roboticSigningSignatures?: {
			documentSetMetadata: {
				[key: string]: {
					name: string;
				};
			};
			session: RoboticSigningSession;
		};
	};
	/**
	 * Map containing all tar-related forms associated with this vehicle.
	 * This map is created after the manual TAR intervention step.
	 * internal-only
	 */
	tarForms?: {
		/**
		 * An array of keys of all forms (besides buyer uploads) that are relevant and will need to be uploaded.
		 */
		formsToSubmit: string[];
		/**
		 * An array of keys of all forms that the buyer must actually sign.
		 */
		formsBuyerMustSign: string[];
		/**
		 * An array of keys of all forms that the buyer must e-sign. The document keys that exist across tarForms and documentsSignatures.
		 */
		formsBuyerMustESign: string[];
		/**
		 * An array of keys of all forms that the buyer must robotic-sign. The document keys that exist in the vosPacket.
		 */
		formsBuyerMustRoboticSign: string[];
		/**
		 * An array of keys of all forms that the buyer must wet-sign. The document keys that exist in the buyerPacket.
		 */
		formsBuyerMustWetSign: string[];
		/**
		 * An array of keys of all forms that the buyer must upload.
		 */
		buyerUploads: string[];
		/**
		 * Timestamp when the seller created and sent the TAR forms to the buyer
		 */
		dateSellerCreated?: number;
		/**
		 * Object of arrays representing each iteration of a physical paperwork workflow.
		 * Each array contains timestamps for when the associated paperwork packet was mailed, received and reviewed.
		 * The key is the name of the packet type.
		 */
		paperworkIterations?: {
			[packetName in keyof typeof DOCUMENT_PACKET_TYPES]: {
				[documentTrackingNumber: string]: {
					/**
					 * The tracking number for the paperwork packet.
					 */
					documentTrackingNumber: string;
					/**
					 * Timestamp when the seller requested the signed TAR paperwork from the buyer.
					 */
					dateSellerRequested?: number;
					/**
					 * The status of the paperwork in the shipment process.
					 */
					shippingStatus?: keyof typeof DOCUMENT_TRACKING_STATUS;
					/**
					 * Timestamp when the buyer mailed back the signed TAR forms.
					 * This starts a new "Receive paperwork" action item workflow.
					 */
					dateBuyerMailed?: number;
					/**
					 * Timestamp when the seller received the signed TAR forms back from the buyer.
					 * This completes a "Receive paperwork" action item and triggers a "Review paperwork" action item.
					 */
					dateSellerReceived?: number;
					/**
					 * Timestamp when the shipping carrier last updated the status of the paperwork.
					 */
					shippingLastActivityTimestamp?: number;
					/**
					 * Timestamp when the shipping carrier is scheduled to deliver the paperwork.
					 */
					scheduledDeliveryDate?: number;
					/**
					 * Timestamp when the seller reviewed the TAR paperwork.
					 * This completes a "Review paperwork" action item. If corrections are needed,
					 * a new "Receive paperwork" action item will be created. Otherwise, triggers
					 * the "Process paperwork" action item.
					 */
					dateSellerReviewed?: number;
					/**
					 * Notes from the seller's review of the TAR paperwork.
					 * This is populated when dateSellerReviewed is set and corrections are needed.
					 * These notes will be used to communicate what corrections are needed to the buyer.
					 */
					reviewNotes?: string;
					/**
					 * The number of times the seller has requested the paperwork from the buyer.
					 */
					attemptNum?: number;
				};
			};
		};
		/**
		 * The timestamp when the seller approved the TAR paperwork after review.
		 * This indicates the paperwork was reviewed and no corrections were needed.
		 * This triggers the "Process paperwork" action item.
		 */
		dateSellerApproved?: number;
		/**
		 * The timestamp when the seller processed the TAR paperwork.
		 * For Vitu workflow: Marks when pre-audit was submitted
		 * For internal workflow: Marks when paperwork was compiled with check
		 * This completes the "Process paperwork" action item and triggers the "Submit paperwork" action item.
		 */
		dateSellerProcessed?: number;
		/**
		 * The timestamp when the seller submitted the TAR paperwork.
		 * For Vitu workflow: Pre-audit submitted and transaction status will be monitored in Vitu dashboard
		 * For internal workflow: Marks when paperwork was mailed to DMV
		 * This completes the "Submit paperwork" action item.
		 */
		dateSellerSubmitted?: number;
	};
	/**
	 * Insurance object that gets created once the vehicle buyer attempts to verify their insurance
	 * internal-only
	 */
	insurance?: {
		/**
		 * The number of times the buyer has pulled their insurance. We cap this at 3.
		 */
		numPulls: number;
		/**
		 * The Canopy Connect pull id.
		 */
		pullId?: string;
		carrierName?: string;
		dateLastPulled: number;
		/**
		 * Gets deleted when buyer wants to re-verify.
		 * An array of strings, each that provide a friendly description of why the insurance is not valid.
		 */
		generalErrors?: string[];
		/**
		 * Gets deleted when buyer wants to re-verify.
		 * An array of objects representing policies and their associated errors.
		 */
		policyErrors?: {
			name: string;
			carrierName: string;
			description: string;
			policyId: string;
			carrierPolicyNumber: string;
			/**
			 * The type of insurance. We use Canopy Connect's enums
			 */
			policyType: string;
			/**
			 * If empty, means the policy is valid.
			 */
			errors: string[];
		}[];
		/**
		 * URL to the manually uploaded insurance document. Is set to public read
		 */
		manualInsuranceDoc?: string;
		/**
		 * Will only not be null if there is a valid policy.
		 */
		validPolicy: {
			/**
			 * Human readable name of the policy
			 */
			name: string;
			/**
			 * Human readable name of the carrier
			 */
			carrierName: string;
			/**
			 * Can be used to grab the policy from Canopy Connect in the future
			 */
			policyId: string;
			/**
			 * The type of insurance. We use Canopy Connect's enums
			 */
			policyType: string;
			/**
			 * The effective date of the policy
			 */
			effectiveDate: number;
			/**
			 * The expiry date of the policy
			 */
			expiryDate: number;
			/**
			 * The carrier's identifier for the policy. Could be null.
			 */
			carrierPolicyNumber: string;
			/**
			 * The two letter state abbreviation of the state the policy was issued in. Could be null.
			 */
			issuingState?: string;
			/**
			 * The name of the agent or broker that sold the policy. Could be null.
			 */
			agentBrokerName?: string;
			/**
			 * Whether the valid policy's validity has been reviewed.
			 */
			hasBeenReviewed: boolean;
		} | null;
		/**
		 * Buyer's selection on the insurance choice page. Will not exist on purchased vehicle until relevant.
		 */
		insuranceSelection?:
			| 'skipInsurance'
			| 'purchaseInsurance'
			| 'useExistingInsurance'
			| 'provideBasicDetails';
	};
	/**
	 * date for when the vehicle has been last updated
	 */
	lastUpdated?: number;
	/**
	 * the last date when the OEM has nudged the buyer (also updated when we send emails for VIN and odometer assigned)
	 */
	lastNudged?: number;
	/**
	 * tracking number for the document set sent by the buyer to us. This will be deprecated in favor of docPacketTracking
	 * internal-only
	 */
	documentTrackingNumber?: string;
	/**
	 * tracking information for physical document sets
	 * internal-only
	 */
	docPacketTracking?: {
		[packetName in keyof typeof DOCUMENT_PACKET_TYPES]: {
			status?: keyof typeof DOCUMENT_TRACKING_STATUS;
			lastActivityTimestamp?: number;
			scheduledDeliveryDate?: number;
			deliveryDate?: number;
		};
	};
	/**
	 *
	 */
	sigWorkflow?: 'ESIGN' | 'WET';
	/** internal-only */
	notaryWorkflow?: 'NONE' | 'ENOTARY' | 'PHYSICAL';
	/** URL to the vehicle invoice */
	vehicleInvoiceURL?: string;
	/**
	 * An optional object that holds additional information about the vehicle.
	 * Example: { randomField: "randomValue" }
	 */
	metadata?: {
		[key: string]: any;
	};
	/**
	 * answers to a Ekho-hosted survey surfaced in buyer portal
	 */
	surveyData?: {
		[key: string]: {
			/** The question being asked */
			question: string;
			answer: string | number | boolean;
		};
	};
	/**
	 * true if the vehicle requires assembly after delivery (optional for backwards compatibility)
	 */
	requiresAssemblyAfterDelivery?: boolean;
	/**
	 * Map of services to an associated services provider id
	 */
	servicesProviders?: { [k in Services]?: string };
	/**
	 * List of services provider ids that are servicing this vehicle
	 */
	servicesProviderIds?: string[];
	/**
	 * Analytics object for the vehicle
	 * for third-party tracking
	 */
	analytics?: {
		google?: {
			clientIds: {
				[key: string]: string;
			};
		};
	};
	accessoriesInventory?: {
		backfillAccessoriesRequest?: boolean;
		dateBackfillAccessoriesRequested?: number;
	};
	inventory?: {
		// use inventory if this is true
		useInventory: boolean;
		// the id of the inventory item that is currently assigned to this vehicle, if any
		inventoryItemId?: string | null;
		// the id of the lock request that is currently associated with this vehicle, if any
		lockRequestId?: string | null;
		// whether we needed to request an inventory item from the manufacturer
		backfillInventoryRequest?: boolean;
		// the date when we last requested an inventory item from the manufacturer
		dateBackfillInventoryRequested?: number;
	};
	warranty?: {
		registration?: {
			required: boolean;
			dateCompleted?: number;
		};
	};
	actionItemSummary?: {
		summaryByAgent: {
			[key in ActionItemAgent]: {
				[key in ActionItemStatus]: number;
			};
		};
		summaryByStatus: {
			[key in ActionItemStatus]: number;
		};
	};
}

/**
 * Common interface for document set metadata
 */
export interface DocumentSetMetadata {
	hasBeenCompleted: boolean;
	documentSetId: string;
	hasBeenCreated: boolean;
	buyerHasSigned: boolean;
	dateBuyerSigned?: number;
	dateCompleted?: number;
	sellerSignatureUrl?: string;
	signatureRequest?: {
		date?: number;
	};
}

/**
 * Common interface for document properties
 */
export interface DocumentProperties {
	name?: string;
	hasBeenCompleted?: boolean;
	documentSetId?: string;
	hasBeenCreated?: boolean;
	buyerHasSigned?: boolean;
}

/**
 * Interface for robotic signing session data
 */
export interface RoboticSigningSession {
	status: keyof typeof ROBOTIC_SIGNING_STATUS;
	signingData: {
		url: string;
	};
	calendarData: {
		eventId: string;
		inviteeId: string;
		metadataId: string;
		startTime: number;
		name: string;
		email: string;
		phone: string;
		cancelUrl: string;
		rescheduleUrl: string;
		hasPromptedMissedSessionRelocFlow: boolean;
		hasPromptedUpcomingSessionRelocFlow: boolean;
	};
}
